import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { formatCents } from '@crave/shared';
import { ApiError } from '@crave/api-client';
import { Button, Card, Spicy } from '@crave/ui';
import { api } from '../api.ts';
import type { PaymentConfig, PaymentSync, Tracking } from '../lib/types.ts';
import { cart } from '../lib/cart.ts';
import { unpaidOrder } from '../lib/unpaid.ts';
import { FeedbackForm } from '../components/Feedback.tsx';
import { FakePayment, StripePayment, preloadPaymentForm } from '../components/StripePayment.tsx';
import { PAYMENT_PENDING, isPaidStatus, syncPaymentOnce } from '../lib/payment.ts';

const TERMINAL = new Set(['COMPLETED', 'CANCELED', 'NO_SHOW', 'REFUNDED', 'PARTIALLY_REFUNDED']);
/** Statuses where the customer can still pay for this order. */
const PAYABLE = new Set([...PAYMENT_PENDING, 'PAYMENT_FAILED']);
/** Query params we add (confirming) or Stripe adds when it sends the customer back. */
const RETURN_PARAMS = [
  'confirming',
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
];
const SYNC_INTERVAL_MS = 2500;
const SYNC_WINDOW_MS = 60_000;

export function TrackPage({ hashed }: { hashed?: boolean }) {
  const { token = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const key = hashed ? `h.${token}` : token;
  const q = useQuery({
    queryKey: ['track', key],
    queryFn: async () => (await api.get<Tracking>(`/track/${key}`)).data,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || TERMINAL.has(d.status)) return false;
      const minutesToPickup = (new Date(d.pickup_at).getTime() - Date.now()) / 60_000;
      return minutesToPickup > 60 ? 30_000 : 10_000;
    },
  });
  const loaded = Boolean(q.data);
  useEffect(() => {
    if (loaded && window.location.hash === '#feedback')
      document.getElementById('feedback')?.scrollIntoView({ behavior: 'smooth' });
  }, [loaded]);
  const cancel = useMutation({
    mutationFn: async () => (await api.post(`/track/${key}/cancel`)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['track', key] }),
    // The order may have moved on since the last refresh: show the reason and the current state.
    onError: () => void qc.invalidateQueries({ queryKey: ['track', key] }),
  });

  // Coming back from checkout or a payment redirect: read once, so tidying the URL keeps the flow.
  const [returned] = useState(() => ({
    confirming:
      params.get('confirming') === '1' ||
      params.has('payment_intent') ||
      params.has('setup_intent'),
  }));
  // The redirect said the payment failed. Cleared once a later payment is accepted here.
  const [redirectFailed, setRedirectFailed] = useState(
    () => params.get('redirect_status') === 'failed',
  );
  const [syncing, setSyncing] = useState(returned.confirming);
  const [syncGaveUp, setSyncGaveUp] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const cartCleared = useRef(false);

  const orderId = q.data?.order_id;
  const status = q.data?.status;

  // Ask the API to read the payment from Stripe instead of waiting on the webhook.
  useEffect(() => {
    if (!syncing || !orderId) return;
    let cancelled = false;
    const started = Date.now();
    void (async () => {
      while (!cancelled) {
        const r = await syncPaymentOnce(orderId);
        if (cancelled) return;
        if (r) {
          setSyncError(r.last_error);
          await qc.invalidateQueries({ queryKey: ['track', key] });
          if (cancelled) return;
          const nothingInFlight =
            r.payment_status === 'FAILED' || r.payment_status === 'REQUIRES_PAYMENT_METHOD';
          if (!PAYMENT_PENDING.has(r.status) || nothingInFlight || redirectFailed) {
            setSyncing(false);
            return;
          }
        }
        if (Date.now() - started >= SYNC_WINDOW_MS) {
          setSyncing(false);
          setSyncGaveUp(true);
          return;
        }
        await new Promise((res) => setTimeout(res, SYNC_INTERVAL_MS));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [syncing, orderId, key, qc, redirectFailed]);

  // Once the order is paid, empty the cart it was placed from, then drop the return params so a
  // reload or bookmark does not repeat any of this.
  useEffect(() => {
    if (!orderId || !status) return;
    // Paid, canceled or done: nothing left to come back and pay.
    if (!PAYABLE.has(status)) unpaidOrder.forget(orderId);
    if (isPaidStatus(status) && !cartCleared.current) {
      cartCleared.current = true;
      if (!cart.clearIfPlaced(orderId) && returned.confirming) cart.clear();
    }
    if (returned.confirming && !syncing && !PAYMENT_PENDING.has(status)) {
      const next = new URLSearchParams(params);
      let changed = false;
      for (const p of RETURN_PARAMS)
        if (next.has(p)) {
          next.delete(p);
          changed = true;
        }
      if (changed) setParams(next, { replace: true });
    }
  }, [orderId, status, syncing, returned.confirming, params, setParams]);

  // The order can be canceled (hold expired) or paid while a retry is open: close the retry.
  useEffect(() => {
    if (status && !PAYABLE.has(status)) setRetrying(false);
  }, [status]);

  if (q.isError)
    return (
      <Card>
        <p style={{ margin: 0 }}>
          {q.error instanceof ApiError ? q.error.message : "We couldn't load this order."}
        </p>
      </Card>
    );
  if (!q.data) return <p>Loading your order…</p>;
  const o = q.data;
  const pending = PAYMENT_PENDING.has(o.status);
  const confirming = syncing && pending;
  const steps = o.stage.steps;
  const payStatus = o.payment?.status;
  const unpaid = o.status === 'PAYMENT_FAILED' || o.status === 'AWAITING_PAYMENT';
  const declined =
    o.status === 'PAYMENT_FAILED' ||
    payStatus === 'FAILED' ||
    (redirectFailed && !isPaidStatus(o.status));
  // Any unpaid order can be paid from here: after a decline, and also when a payment was started
  // and left unfinished (a Cash App QR code never scanned, a bank page closed). Only a payment the
  // bank is still processing waits.
  const canPay = !syncing && unpaid && payStatus !== 'PROCESSING';
  const showPay = canPay || (retrying && PAYABLE.has(o.status));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <style>{PAY_CSS}</style>
      {showPay ? (
        <PayAgain
          o={o}
          declined={declined}
          message={syncError ?? o.payment?.last_error ?? null}
          canCancel={o.can_cancel}
          canceling={cancel.isPending}
          onCancelOrder={() => {
            if (confirm('Cancel this order? Nothing will be charged.')) cancel.mutate();
          }}
          onStart={() => setRetrying(true)}
          onCancel={() => {
            setRetrying(false);
            void qc.invalidateQueries({ queryKey: ['track', key] });
          }}
          onSettled={(r) => {
            setRetrying(false);
            setRedirectFailed(false);
            if (params.has('redirect_status')) {
              const next = new URLSearchParams(params);
              next.delete('redirect_status');
              setParams(next, { replace: true });
            }
            if (r?.last_error) setSyncError(r.last_error);
            void qc.invalidateQueries({ queryKey: ['track', key] });
            // Still waiting on the bank: keep checking here.
            if (!r || PAYMENT_PENDING.has(r.status)) setSyncing(true);
          }}
        />
      ) : (
        <div>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>Order {o.order_number}</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 30 }}>
            {confirming ? 'Confirming your payment…' : o.stage.title}
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-soft)' }}>
            {confirming ? 'This takes a few seconds.' : o.stage.detail}
          </p>
        </div>
      )}
      {o.status === 'CANCELED' && o.cancellation && (
        <section className="pay-sheet" aria-label="Why this order was canceled">
          <div style={{ display: 'grid', gap: 14 }}>
            <div>
              <div className="pay-order" style={{ fontWeight: 600 }}>
                Why
              </div>
              <p style={{ margin: '2px 0 0' }}>
                {o.cancellation.why}
                {o.cancellation.staffNote ? ` ${o.cancellation.staffNote}` : ''}
              </p>
            </div>
            <div>
              <div className="pay-order" style={{ fontWeight: 600 }}>
                Your money
              </div>
              <p style={{ margin: '2px 0 0' }}>{o.cancellation.money}</p>
            </div>
            <div>
              <div className="pay-order" style={{ fontWeight: 600 }}>
                What&apos;s next
              </div>
              <p style={{ margin: '2px 0 0' }}>{o.cancellation.next}</p>
            </div>
          </div>
          {o.cancellation.orderAgain && (
            <Link to={cart.get().length ? '/cart' : '/'} className="pay-primary">
              Order Again
            </Link>
          )}
          {o.cancellation.textUs && (
            <a href={`sms:+1${o.cancellation.textUs.replace(/\D/g, '')}`} className="pay-secondary">
              Questions? Text us at {o.cancellation.textUs}
            </a>
          )}
        </section>
      )}
      {o.stage.step >= 0 && !unpaid && (
        <ol
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
            gap: 4,
            padding: 0,
            margin: 0,
            listStyle: 'none',
          }}
          aria-label="Progress"
        >
          {steps.map((s, i) => (
            <li key={s} style={{ display: 'grid', gap: 6 }}>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: i <= o.stage.step ? 'var(--crave-red)' : 'var(--line)',
                }}
              />
              <span
                style={{
                  fontSize: 10,
                  color: i <= o.stage.step ? 'var(--ink)' : 'var(--ink-soft)',
                  textAlign: 'center',
                }}
              >
                {s}
              </span>
            </li>
          ))}
        </ol>
      )}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Pickup</span>
          <strong>{o.pickup_label}</strong>
        </div>
        {o.eta_label && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span>Estimated ready</span>
            <strong>{o.eta_label}</strong>
          </div>
        )}
        <div style={{ marginTop: 6, color: 'var(--ink-soft)' }}>
          {o.location.name}
          {o.location.pickup_instructions ? ` · ${o.location.pickup_instructions}` : ''}
        </div>
        {/* A Neapolitan is soft in the middle and best within minutes: worth saying before it lands. */}
        <div style={{ marginTop: 10 }}>
          <Link
            to="/how-to-eat"
            style={{ fontWeight: 600, fontSize: 14, color: 'var(--crave-red)' }}
          >
            How to eat a Neapolitan pizza
          </Link>
        </div>
      </Card>
      {syncGaveUp && pending && !syncing && !canPay && (
        <Card>
          <p style={{ margin: 0 }}>
            We are still checking your payment. This page updates on its own.
          </p>
          <div style={{ marginTop: 8 }}>
            <Button
              variant="secondary"
              onClick={() => {
                setSyncGaveUp(false);
                setSyncing(true);
              }}
            >
              Check again
            </Button>
          </div>
        </Card>
      )}
      <Card>
        {o.items.map((i, idx) => (
          <div
            key={idx}
            style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}
          >
            <span>
              {i.quantity} × {i.name}
              {i.spicy && (
                <>
                  {' '}
                  <Spicy size="sm" />
                </>
              )}
              {i.modifiers.length ? (
                <span style={{ color: 'var(--ink-soft)' }}>
                  {' '}
                  · {i.modifiers.map((m) => m.name).join(', ')}
                </span>
              ) : null}
            </span>
            <span>{formatCents(i.line_total_cents)}</span>
          </div>
        ))}
        <div
          style={{
            borderTop: '1px solid var(--line)',
            marginTop: 8,
            paddingTop: 8,
            display: 'grid',
            gap: 4,
            fontSize: 14,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Food</span>
            <span>{formatCents(o.totals.subtotal_cents)}</span>
          </div>
          {o.totals.discount_cents > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{o.birthday_applied ? 'Birthday pizza' : 'Discount'}</span>
              <span>−{formatCents(o.totals.discount_cents)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Tax</span>
            <span>{formatCents(o.totals.tax_cents)}</span>
          </div>
          {o.totals.tip_cents > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Tip</span>
              <span>{formatCents(o.totals.tip_cents)}</span>
            </div>
          )}
          {o.totals.credit_applied_cents > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Crave Credit</span>
              <span>−{formatCents(o.totals.credit_applied_cents)}</span>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            <span>{o.receipt_available ? 'Charged' : 'Total at pickup'}</span>
            <span>{formatCents(o.totals.amount_due_cents)}</span>
          </div>
        </div>
        {o.receipt_available && (
          <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>
            This page is your receipt. A copy was emailed to {o.customer.email}.
          </p>
        )}
        {o.credit_earned_cents > 0 && (
          <p style={{ margin: '8px 0 0', fontWeight: 600 }}>
            You earned {formatCents(o.credit_earned_cents)} in Crave Credit on this order.
          </p>
        )}
      </Card>
      <div id="feedback">
        {o.feedback.can_submit && (
          <FeedbackForm
            title="How was it?"
            pizzas={o.items.map((i) => ({
              id: i.id,
              name: i.quantity > 1 ? `${i.name} × ${i.quantity}` : i.name,
              quantity: i.quantity,
            }))}
            onSubmit={async (body) => {
              await api.post(`/track/${key}/feedback`, body);
              void qc.invalidateQueries({ queryKey: ['track', key] });
            }}
          />
        )}
        {o.feedback.submitted && (
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>Thanks for rating this order.</p>
        )}
      </div>
      {(o.can_cancel || o.can_request_cancel) && !showPay && (
        <div>
          <Button
            variant="danger"
            onClick={() => {
              if (
                confirm(
                  o.can_cancel
                    ? 'Cancel this order? Nothing will be charged.'
                    : 'We already started preparing this. Ask the kitchen to cancel?',
                )
              )
                cancel.mutate();
            }}
            disabled={cancel.isPending}
          >
            {cancel.isPending
              ? 'Canceling…'
              : o.can_cancel
                ? 'Cancel order'
                : 'Request cancellation'}
          </Button>
          {o.can_cancel && (
            <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: '8px 0 0' }}>
              {o.policies.cancel}
            </p>
          )}
        </div>
      )}
      {cancel.isError && (
        <p role="alert" style={{ color: 'var(--crave-red)', margin: 0 }}>
          {cancel.error instanceof ApiError
            ? cancel.error.message
            : 'We could not cancel this order.'}{' '}
          {o.location.phone
            ? `Call us at ${o.location.phone} if you still need to cancel.`
            : 'Please call us if you still need to cancel.'}
        </p>
      )}
      {o.timeline.length > 0 && (
        <details>
          <summary
            style={{
              cursor: 'pointer',
              // Padding, not flex: a flex summary loses its disclosure arrow in Safari.
              padding: '10px 0',
            }}
          >
            Timeline
          </summary>
          <ul
            style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--ink-soft)', fontSize: 14 }}
          >
            {o.timeline.map((t, i) => (
              <li key={i}>
                {new Date(t.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} ·{' '}
                {t.title}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

const PAY_CSS = `
.pay-sheet { background: var(--paper); border-radius: 22px; padding: 24px 20px 20px; display: grid; gap: 14px;
  box-shadow: 0 1px 2px rgba(20,20,20,.05), 0 12px 32px rgba(20,20,20,.08); }
.pay-top { display: grid; justify-items: center; text-align: center; gap: 6px; }
.pay-icon { width: 56px; height: 56px; border-radius: 50%; display: grid; place-items: center; margin-bottom: 4px; }
.pay-icon[data-tone='failed'] { background: #fdecec; color: var(--crave-red); }
.pay-icon[data-tone='waiting'] { background: var(--mist); color: var(--ink); }
.pay-order { font-size: 13px; color: var(--ink-soft); }
.pay-title { margin: 0; font-size: 26px; line-height: 1.15; font-weight: 700; letter-spacing: -.01em; }
.pay-lead { margin: 0; color: var(--ink-soft); max-width: 36ch; }
.pay-reason { margin: 0; font-size: 13px; color: var(--ink-soft); }
.pay-hold { justify-self: center; display: inline-block; text-align: center; padding: 6px 14px;
  border-radius: 999px; background: var(--mist); font-size: 14px; font-weight: 600; }
.pay-hold[data-urgent] { background: #fdecec; color: var(--crave-red); }
.pay-hold time { font-variant-numeric: tabular-nums; }
.pay-primary { display: flex; align-items: center; justify-content: center; min-height: 52px; border-radius: 14px;
  border: 0; background: var(--crave-red); color: #fff; font: inherit; font-size: 17px; font-weight: 600;
  text-decoration: none; cursor: pointer; width: 100%; }
.pay-primary:disabled { opacity: .6; cursor: default; }
.pay-secondary { display: flex; align-items: center; justify-content: center; min-height: 44px; border: 0;
  background: none; color: var(--crave-red); font: inherit; font-weight: 600; cursor: pointer; width: 100%; }
.pay-methods { margin: 0; text-align: center; font-size: 13px; color: var(--ink-soft); }
.pay-form { display: grid; gap: 12px; border-top: 1px solid var(--line); padding-top: 16px; }
.pay-form h2 { margin: 0; font-size: 17px; }
`;

/** "11:32" until the hold ends, ticking every second; null once it has ended. */
function useCountdown(until: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  if (!until) return null;
  const left = Math.floor((new Date(until).getTime() - now) / 1000);
  if (left <= 0) return null;
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

/** Stripe's words are about cards even for Cash App Pay; keep them as a small detail only. */
function friendlyReason(message: string | null): string | null {
  if (!message) return null;
  const m = message.trim();
  return m.length > 160 ? `${m.slice(0, 157)}…` : m;
}

/**
 * Pay for this same order after a decline or an unfinished payment, laid out like an Apple sheet:
 * what happened, how long the pickup time is held, one clear button. Reopens the order's existing
 * payment through retry-payment, so its pickup time, credit and birthday pizza stay put, and the
 * payment form offers every way to pay that Stripe has turned on for the site.
 */
function PayAgain({
  o,
  declined,
  message,
  canCancel,
  canceling,
  onCancelOrder,
  onStart,
  onCancel,
  onSettled,
}: {
  o: Tracking;
  declined: boolean;
  message: string | null;
  canCancel: boolean;
  canceling: boolean;
  onCancelOrder: () => void;
  onStart: () => void;
  onCancel: () => void;
  onSettled: (r: PaymentSync | null) => void;
}) {
  const [session, setSession] = useState<PaymentSync | null>(null);
  const config = useQuery({
    queryKey: ['pay-config'],
    queryFn: async () => (await api.get<PaymentConfig>('/public/payments/config')).data,
  });
  /**
   * Fetch the payment form while this sheet is on screen, before the customer taps: the pages
   * before this one never load Stripe, so the form is a download that has to start somewhere.
   */
  const stripeProvider = config.data?.provider === 'stripe';
  useEffect(() => {
    if (stripeProvider) preloadPaymentForm();
  }, [stripeProvider]);
  const start = useMutation({
    mutationFn: async () =>
      (await api.post<PaymentSync>(`/orders/${o.order_id}/retry-payment`)).data,
    onMutate: () => onStart(),
    onSuccess: (r) => setSession(r),
    onError: () => onCancel(),
  });
  const left = useCountdown(o.hold_expires_at);
  const urgent = left !== null && Number(left.split(':')[0]) < 3;

  const returnUrl = `${window.location.origin}${window.location.pathname}?confirming=1`;
  const cfg = config.data;
  const reason = declined ? friendlyReason(message) : null;

  return (
    <section className="pay-sheet" aria-labelledby="pay-title">
      <div className="pay-top">
        <span className="pay-icon" data-tone={declined ? 'failed' : 'waiting'} aria-hidden="true">
          {declined ? (
            <svg width="28" height="28" viewBox="0 0 24 24">
              <path
                d="M12 7v6m0 4h.01"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2" />
              <path
                d="M12 7.5V12l3 2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </span>
        <span className="pay-order">
          Order {o.order_number} · {formatCents(o.totals.amount_due_cents)}
        </span>
        <h1 className="pay-title" id="pay-title">
          {declined ? "Payment didn't go through" : 'Finish your payment'}
        </h1>
        <p className="pay-lead">
          {declined
            ? 'Nothing was charged and your order is not confirmed yet. Try again, or pay another way.'
            : 'Your order is saved but not paid yet. Pay to lock in your pickup time.'}
        </p>
        {reason && <p className="pay-reason">Details: {reason}</p>}
      </div>
      {o.hold_label && (
        <div className="pay-hold" data-urgent={urgent ? '' : undefined} role="status">
          {left ? (
            <>
              Pickup {o.pickup_label.split(' · ').pop()} held · <time>{left}</time> left
            </>
          ) : (
            <>Your hold is ending. Pay now to keep this pickup time if it is still open.</>
          )}
        </div>
      )}
      {!session ? (
        <>
          <button
            type="button"
            className="pay-primary"
            onClick={() => start.mutate()}
            disabled={start.isPending}
          >
            {start.isPending ? 'Opening payment…' : declined ? 'Try Again' : 'Finish Payment'}
          </button>
          <p className="pay-methods">Card, Cash App Pay and more</p>
          {start.isError && (
            <p role="alert" style={{ color: 'var(--crave-red)', margin: 0, textAlign: 'center' }}>
              {start.error instanceof ApiError
                ? start.error.message
                : 'We could not reopen your payment. Please try again.'}
            </p>
          )}
        </>
      ) : (
        <div className="pay-form">
          <h2>Choose how to pay</h2>
          {config.isLoading ? (
            <p style={{ margin: 0 }}>Loading secure payment…</p>
          ) : cfg?.provider === 'stripe' &&
            cfg.publishable_key &&
            session.client_secret &&
            session.mode !== 'none' ? (
            <StripePayment
              publishableKey={cfg.publishable_key}
              clientSecret={session.client_secret}
              orderId={o.order_id}
              mode={session.mode}
              amountCents={o.totals.amount_due_cents}
              email={o.customer.email}
              returnUrl={returnUrl}
              onSettled={(r) => {
                setSession(null);
                onSettled(r);
              }}
            />
          ) : cfg?.provider === 'fake' ? (
            <FakePayment
              orderId={o.order_id}
              onSettled={(r) => {
                setSession(null);
                onSettled(r);
              }}
            />
          ) : (
            <p style={{ color: 'var(--crave-red)', margin: 0 }}>
              Online payment is not available right now.{' '}
              {o.location.phone ? `Call us at ${o.location.phone}.` : 'Please call us.'}
            </p>
          )}
        </div>
      )}
      {canCancel && (
        <button
          type="button"
          className="pay-secondary"
          onClick={onCancelOrder}
          disabled={canceling}
        >
          {canceling ? 'Canceling…' : 'Cancel Order'}
        </button>
      )}
    </section>
  );
}
